import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { sportsMenuFullQuery } from '@/api/queries';
import { translateSportsLabel } from '@/i18n/sports-label';
import { resolveSport, toSlug } from '@/shell/sports-menu-utils';
import {
  DEFAULT_DAY_FILTER_OFFSET,
  DayFilterMenu,
  EmptyState,
  buildDayFilterOptions,
  dayRange,
  type DayFilterOffset,
} from '@/ui';
import { EventsFeed, EventsFeedSkeleton } from './-components/EventsFeed';

export const Route = createFileRoute('/sports/$sport/today')({
  component: TodayPage,
});

function TodayPage() {
  const { sport: sportSlug } = Route.useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const menuQuery = useQuery(sportsMenuFullQuery());
  const [dayOffset, setDayOffset] = useState<DayFilterOffset>(DEFAULT_DAY_FILTER_OFFSET);

  const sport = useMemo(
    () => resolveSport(menuQuery.data, sportSlug),
    [menuQuery.data, sportSlug],
  );

  const dayOptions = useMemo(
    () =>
      buildDayFilterOptions(new Date(), {
        locale: i18n.language,
        todayLabel: t('Today', { defaultValue: 'Today' }),
        tomorrowLabel: t('Tomorrow', { defaultValue: 'Tomorrow' }),
      }),
    [i18n.language, t],
  );

  const pageTitle =
    dayOptions.find((option) => option.offset === dayOffset)?.label ??
    t('Today', { defaultValue: 'Today' });

  const filter = useMemo(() => {
    if (!sport) return null;
    const range = dayRange(dayOffset);
    return {
      Sport: sport.Name,
      DateFrom: range.DateFrom,
      DateTo: range.DateTo,
    };
  }, [sport, dayOffset]);

  if (menuQuery.isPending) {
    return <EventsFeedSkeleton />;
  }

  if (!sport || !filter) {
    return (
      <EmptyState
        icon="football"
        title={t('SportNotFound', { defaultValue: 'Sport not found' })}
        description={t('SportNotInMenu', {
          defaultValue: 'This sport is not in the current sports menu.',
        })}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <DayFilterMenu value={dayOffset} onValueChange={setDayOffset} />
      <EventsFeed
        scope="today"
        filter={filter}
        title={pageTitle}
        subtitle={translateSportsLabel(i18n, t, sport.Id, sport.Name)}
        emptyDescription={t('NoEventsForDay', {
          defaultValue: 'No events available for this day right now.',
        })}
        onOpenEvent={({ eventId, sport: eventSport, category, tournament, offer }) => {
          const resolvedSport = eventSport ? toSlug(eventSport) : sportSlug;
          const categorySlug = category ? toSlug(category) : undefined;
          const tournamentSlug = tournament ? toSlug(tournament) : undefined;
          if (!categorySlug || !tournamentSlug) return;
          void navigate({
            to: '/sports/$sport/$category/$tournament/$eventId',
            params: {
              sport: resolvedSport,
              category: categorySlug,
              tournament: tournamentSlug,
              eventId: String(eventId),
            },
            search: { offer },
          });
        }}
      />
    </div>
  );
}
